import styles from './TextInputShared.module.scss';

import {
  useContext,
  useEffect,
  useRef,
  type ChangeEvent,
  type Ref,
} from 'react';
import { useMergeRefs } from '@libs/react/ref';
import { FormContext } from '../form/FormContext';
import { FormFieldContext } from '../form/FormFieldContext';
import { FormFieldInputContext } from '../form';
import { InputContainer } from './InputContainer';

interface Props {
  defaultValue?: string;
  label: string;
  lines?: number;
  maxLength?: number;
  multiline?: boolean;
  name?: string;
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onValue?: (value: string) => void;
  ref?: Ref<HTMLTextAreaElement>;
  value?: string;
}

export function TextAreaInput(props: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const mergedRef = useMergeRefs(props.ref, ref);
  const formContext = useContext(FormContext);
  const formFieldContext = useContext(FormFieldContext);
  const formFieldInputContext = useContext(FormFieldInputContext);
  const rows = props.lines ?? 3;
  const clasName = styles['input'];

  const name = props.name ?? formFieldContext.name;

  function onChange(event: ChangeEvent<HTMLTextAreaElement>) {
    props.onChange?.(event);
    props.onValue?.(event.currentTarget.value);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (props.multiline === false && event.key === 'Enter') {
      event.preventDefault();
    }
  }

  useEffect(() => {
    if (name && ref.current) {
      const prev = formContext.inputs.get(name) ?? [];
      formContext.inputs.set(name, [...prev, ref.current]);
      formFieldInputContext?.set(name, ref.current);
      return () => {
        formContext.inputs.delete(name);
      };
    }
  }, [formContext, name, formFieldInputContext]);

  return (
    <InputContainer label={props.label}>
      {({ id }) => {
        return (
          <textarea
            ref={mergedRef}
            className={clasName}
            defaultValue={props.defaultValue}
            id={id}
            maxLength={props.maxLength}
            name={name}
            onChange={onChange}
            onKeyDown={onKeyDown}
            rows={rows}
            value={props.value}
          />
        );
      }}
    </InputContainer>
  );
}
